import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const SPEED_OPTIONS = [1, 1.25, 1.5, 2] as const;
function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
export function VideoPlayer({
  src
}: {
  src: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const volumeSliderRef = useRef<HTMLDivElement>(null);
  // State
  const [hasStarted, setHasStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(0.8);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [buffered, setBuffered] = useState(0);
  // Refs to avoid stale closures in setTimeout
  const isPausedRef = useRef(isPaused);
  const showSpeedMenuRef = useRef(showSpeedMenu);
  const showVolumeSliderRef = useRef(showVolumeSlider);
  const isScrubbingRef = useRef(isScrubbing);
  // Keep refs in sync with state
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);
  useEffect(() => {
    showSpeedMenuRef.current = showSpeedMenu;
  }, [showSpeedMenu]);
  useEffect(() => {
    showVolumeSliderRef.current = showVolumeSlider;
  }, [showVolumeSlider]);
  useEffect(() => {
    isScrubbingRef.current = isScrubbing;
  }, [isScrubbing]);
  // Initial play click — unmute and show controls
  const handleInitialPlay = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.play();
      setIsMuted(false);
      setHasStarted(true);
      setShowControls(true);
      resetControlsTimeout();
    }
  };
  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPaused(false);
    } else {
      videoRef.current.pause();
      setIsPaused(true);
    }
    resetControlsTimeout();
  }, []);
  // Toggle mute
  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    if (isMuted) {
      videoRef.current.muted = false;
      videoRef.current.volume = volume;
      setIsMuted(false);
    } else {
      videoRef.current.muted = true;
      setIsMuted(true);
    }
    resetControlsTimeout();
  }, [isMuted, volume]);
  // Change volume
  const handleVolumeChange = useCallback((newVolume: number) => {
    if (!videoRef.current) return;
    const clamped = Math.max(0, Math.min(1, newVolume));
    videoRef.current.volume = clamped;
    videoRef.current.muted = clamped === 0;
    setVolume(clamped);
    setIsMuted(clamped === 0);
    resetControlsTimeout();
  }, []);
  // Change speed
  const handleSpeedChange = useCallback((rate: number) => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = rate;
    setPlaybackRate(rate);
    setShowSpeedMenu(false);
    resetControlsTimeout();
  }, []);
  // Scrub timeline
  const handleScrub = useCallback((e: React.MouseEvent<HTMLDivElement> | MouseEvent) => {
    if (!progressRef.current || !videoRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const pct = x / rect.width;
    const newTime = pct * duration;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);
  const handleScrubStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setIsScrubbing(true);
    handleScrub(e);
  }, [handleScrub]);
  // Mouse move/up for scrubbing
  useEffect(() => {
    if (!isScrubbing) return;
    const handleMouseMove = (e: MouseEvent) => handleScrub(e);
    const handleMouseUp = () => setIsScrubbing(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isScrubbing, handleScrub]);
  // Auto-hide controls
  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    setShowControls(true);
    controlsTimeoutRef.current = setTimeout(() => {
      if (!isPausedRef.current && !showSpeedMenuRef.current && !showVolumeSliderRef.current && !isScrubbingRef.current) {
        setShowControls(false);
      }
    }, 3000);
  }, []);
  // Keep controls visible when paused or menus open
  useEffect(() => {
    if (isPaused || showSpeedMenu || showVolumeSlider || isScrubbing) {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      setShowControls(true);
    }
  }, [isPaused, showSpeedMenu, showVolumeSlider, isScrubbing]);
  // Close menus on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (showSpeedMenu) setShowSpeedMenu(false);
    };
    if (showSpeedMenu) {
      document.addEventListener('click', handleClick, {
        capture: true
      });
      return () => document.removeEventListener('click', handleClick, {
        capture: true
      });
    }
  }, [showSpeedMenu]);
  // Video event handlers
  const handleTimeUpdate = () => {
    if (!videoRef.current || isScrubbing) return;
    setCurrentTime(videoRef.current.currentTime);
    // Update buffered
    if (videoRef.current.buffered.length > 0) {
      setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length - 1));
    }
  };
  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    setDuration(videoRef.current.duration);
  };
  const progressPct = duration > 0 ? currentTime / duration * 100 : 0;
  const bufferedPct = duration > 0 ? buffered / duration * 100 : 0;
  return <div className="relative w-full rounded-lg overflow-hidden border-2 border-cyan-500/40 bg-black group" style={{
    aspectRatio: '16 / 9',
    boxShadow: '0 0 50px rgba(34, 211, 238, 0.2), 0 0 100px rgba(34, 211, 238, 0.08)'
  }} onMouseMove={hasStarted ? resetControlsTimeout : undefined} onMouseEnter={hasStarted ? () => setShowControls(true) : undefined} onMouseLeave={hasStarted ? () => {
    if (!isPaused && !showSpeedMenu && !showVolumeSlider && !isScrubbing) {
      setShowControls(false);
    }
  } : undefined} data-id="element-5052">
      <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover cursor-pointer" src={src} autoPlay muted loop playsInline preload="auto" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onPlay={() => setIsPaused(false)} onPause={() => setIsPaused(true)} onClick={hasStarted ? togglePlayPause : undefined} data-id="element-5053" />

      {/* Initial play overlay — before user clicks */}
      <AnimatePresence data-id="element-5054">
        {!hasStarted && <motion.div className="absolute inset-0 flex flex-col items-center justify-center z-20 cursor-pointer bg-black/40" onClick={handleInitialPlay} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-5055">
            <div className="relative mb-4" data-id="element-5056">
              <motion.div className="absolute inset-0 bg-cyan-500/30 blur-3xl rounded-full scale-[2]" animate={{
            scale: [2, 2.5, 2],
            opacity: [0.3, 0.5, 0.3]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-5057" />
              <motion.div className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full border-3 border-cyan-400/80 bg-black/60 flex items-center justify-center backdrop-blur-sm" whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.95
          }} animate={{
            boxShadow: ['0 0 20px rgba(34, 211, 238, 0.3), 0 0 60px rgba(34, 211, 238, 0.1)', '0 0 40px rgba(34, 211, 238, 0.5), 0 0 80px rgba(34, 211, 238, 0.2)', '0 0 20px rgba(34, 211, 238, 0.3), 0 0 60px rgba(34, 211, 238, 0.1)']
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-5058">
                <svg className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-cyan-400 ml-1" fill="currentColor" viewBox="0 0 24 24" data-id="element-5059">
                  <path d="M8 5v14l11-7z" data-id="element-5060" />
                </svg>
              </motion.div>
            </div>
            <motion.p className="text-cyan-300 font-bold text-sm sm:text-base md:text-lg tracking-wide uppercase" animate={{
          opacity: [0.7, 1, 0.7]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} style={{
          textShadow: '0 0 20px rgba(34, 211, 238, 0.5)'
        }} data-id="element-5061">
              ▶ Tap to Play with Sound
            </motion.p>
          </motion.div>}
      </AnimatePresence>

      {/* Center play/pause indicator on click */}
      <AnimatePresence data-id="element-5062">
        {hasStarted && isPaused && <motion.div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-5063">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center" data-id="element-5064">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24" data-id="element-5065">
                <path d="M8 5v14l11-7z" data-id="element-5066" />
              </svg>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Custom Controls Bar */}
      <AnimatePresence data-id="element-5067">
        {hasStarted && showControls && <motion.div className="absolute bottom-0 left-0 right-0 z-30" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 10
      }} transition={{
        duration: 0.2
      }} onClick={e => e.stopPropagation()} data-id="element-5068">
            {/* Gradient fade behind controls */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none" style={{
          top: '-40px'
        }} data-id="element-5069" />

            <div className="relative px-3 sm:px-4 pb-2.5 sm:pb-3 pt-6" data-id="element-5070">
              {/* Timeline / Progress Bar */}
              <div ref={progressRef} className="w-full h-1.5 sm:h-2 bg-white/20 rounded-full cursor-pointer mb-2.5 sm:mb-3 relative group/progress hover:h-2.5 sm:hover:h-3 transition-all duration-150" onMouseDown={handleScrubStart} data-id="element-5071">
                {/* Buffered */}
                <div className="absolute inset-y-0 left-0 bg-white/20 rounded-full" style={{
              width: `${bufferedPct}%`
            }} data-id="element-5072" />
                {/* Progress */}
                <div className="absolute inset-y-0 left-0 bg-cyan-400 rounded-full" style={{
              width: `${progressPct}%`
            }} data-id="element-5073" />
                {/* Scrub handle */}
                <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 sm:w-3.5 sm:h-3.5 bg-cyan-400 rounded-full shadow-[0_0_8px_rgba(34,211,238,0.6)] opacity-0 group-hover/progress:opacity-100 transition-opacity" style={{
              left: `${progressPct}%`,
              transform: 'translate(-50%, -50%)'
            }} data-id="element-5074" />
              </div>

              {/* Controls Row */}
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-5075">
                {/* Play/Pause */}
                <button onClick={togglePlayPause} className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white hover:text-cyan-400 transition-colors" aria-label={isPaused ? 'Play' : 'Pause'} data-id="element-5076">
                  {isPaused ? <svg className="w-5 h-5 sm:w-6 sm:h-6 ml-0.5" fill="currentColor" viewBox="0 0 24 24" data-id="element-5077">
                      <path d="M8 5v14l11-7z" data-id="element-5078" />
                    </svg> : <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24" data-id="element-5079">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" data-id="element-5080" />
                    </svg>}
                </button>

                {/* Volume */}
                <div className="relative flex items-center flex-shrink-0" data-id="element-5081">
                  <button onClick={toggleMute} onMouseEnter={() => setShowVolumeSlider(true)} className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white hover:text-cyan-400 transition-colors" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-5082">
                    {isMuted || volume === 0 ? <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24" data-id="element-5083">
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" data-id="element-5084" />
                      </svg> : volume < 0.5 ? <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24" data-id="element-5085">
                        <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" data-id="element-5086" />
                      </svg> : <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24" data-id="element-5087">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" data-id="element-5088" />
                      </svg>}
                  </button>

                  {/* Volume Slider */}
                  <AnimatePresence data-id="element-5089">
                    {showVolumeSlider && <motion.div ref={volumeSliderRef} className="flex items-center ml-1" initial={{
                  width: 0,
                  opacity: 0
                }} animate={{
                  width: 'auto',
                  opacity: 1
                }} exit={{
                  width: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.15
                }} onMouseEnter={() => setShowVolumeSlider(true)} onMouseLeave={() => setShowVolumeSlider(false)} data-id="element-5090">
                        <input type="range" min="0" max="1" step="0.05" value={isMuted ? 0 : volume} onChange={e => handleVolumeChange(parseFloat(e.target.value))} className="w-16 sm:w-20 h-1 accent-cyan-400 cursor-pointer" style={{
                    background: `linear-gradient(to right, #22d3ee ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.2) ${(isMuted ? 0 : volume) * 100}%)`
                  }} data-id="element-5091" />
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Time Display */}
                <span className="text-white/80 text-xs sm:text-sm font-mono tabular-nums flex-shrink-0 select-none" data-id="element-5092">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>

                {/* Spacer */}
                <div className="flex-1" data-id="element-5093" />

                {/* Speed Control */}
                <div className="relative flex-shrink-0" data-id="element-5094">
                  <button onClick={e => {
                e.stopPropagation();
                setShowSpeedMenu(!showSpeedMenu);
              }} className="px-2 py-1 text-xs sm:text-sm font-bold text-white hover:text-cyan-400 transition-colors rounded border border-white/20 hover:border-cyan-400/50 min-w-[40px] sm:min-w-[48px] text-center" aria-label="Playback speed" data-id="element-5095">
                    {playbackRate}x
                  </button>

                  <AnimatePresence data-id="element-5096">
                    {showSpeedMenu && <motion.div className="absolute bottom-full right-0 mb-2 bg-stone-900/95 backdrop-blur-md border border-white/20 rounded-lg overflow-hidden shadow-xl" initial={{
                  opacity: 0,
                  y: 5
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 5
                }} transition={{
                  duration: 0.15
                }} onClick={e => e.stopPropagation()} data-id="element-5097">
                        {SPEED_OPTIONS.map(speed => <button key={speed} onClick={() => handleSpeedChange(speed)} className={`block w-full px-4 py-2 text-sm text-left transition-colors whitespace-nowrap ${playbackRate === speed ? 'text-cyan-400 bg-cyan-400/10' : 'text-white hover:bg-white/10'}`} data-id="element-5098">
                            {speed}x {speed === 1 ? '(Normal)' : ''}
                          </button>)}
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Fullscreen */}
                <button onClick={() => {
              const container = videoRef.current?.parentElement;
              if (container) {
                if (document.fullscreenElement) {
                  document.exitFullscreen();
                } else {
                  container.requestFullscreen();
                }
              }
            }} className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white hover:text-cyan-400 transition-colors" aria-label="Fullscreen" data-id="element-5099">
                  <svg className="w-4.5 h-4.5 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-5100">
                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" data-id="element-5101" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}